<script setup>
import { computed } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <!--
    SZ: animação na aparição/colapso da coluna de Detalhes de Contato.
    Comportamento original usava `md:hidden` (display:none) — não anima.
    Em vez de classes Tailwind dinâmicas (JIT não detecta classes vindas
    de ternários longos), usamos um attribute `data-open` e regras CSS
    scoped abaixo: animam width/opacity/transform com transition-all 300ms.
  -->
  <div
    v-on-click-outside="() => closeContactPanel()"
    class="sz-contact-sidebar bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-all duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:flex md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :data-open="activeTab === 0 ? 'true' : 'false'"
  >
    <!--
      SZ: ContactPanel sempre montado. O `v-show` original aplicava
      `display:none` instantâneo quando fechava — roubando a animação
      do wrapper. Agora o wrapper anima width/opacity/transform por 300ms
      e o conteúdo permanece visível durante toda a transição.
    -->
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Closed state — overrides Tailwind utilities via !important since the base
// classes already set width/border. Uses `[data-open="false"]` so we don't
// rely on Tailwind JIT picking up md:* utilities from a dynamic expression.
.sz-contact-sidebar[data-open='false'] {
  opacity: 0;
  pointer-events: none;
}

@media (min-width: 768px) {
  .sz-contact-sidebar[data-open='false'] {
    width: 0 !important;
    min-width: 0 !important;
    border-left-width: 0 !important;
    border-right-width: 0 !important;
    transform: translateX(100%);
  }

  // RTL: slides off the left side instead of the right
  [dir='rtl'] .sz-contact-sidebar[data-open='false'] {
    transform: translateX(-100%);
  }
}
</style>
